import { forwardRef } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { Button, ButtonProps } from '../../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';

interface CloseIconButtonProps extends Omit<ButtonProps, 'children'> {
  tooltip?: string;
  showTooltip?: boolean;
}

/**
 * Standardized close icon button for dialogs and panels.
 */
export const CloseIconButton = forwardRef<HTMLButtonElement, CloseIconButtonProps>(
  ({ tooltip, showTooltip = true, className, ...props }, ref) => {
    const { t } = useTranslation('common');
    const label = tooltip || t('app.close');

    const button = (
      <Button
        ref={ref}
        variant="ghost"
        size="icon"
        aria-label={label}
        className={cn(className)}
        {...props}
      >
        <X className="h-4 w-4" />
      </Button>
    );

    if (!showTooltip) return button;

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    );
  }
);

CloseIconButton.displayName = 'CloseIconButton';
